import axios from "axios";
import { toast } from "react-toastify";
import { API_BASE_PATH } from "../environment";

// ✅ Base URL
const BASE_URL = `${API_BASE_PATH}/producttype`;

// ✅ ProductType interface
export interface ProductType {
  product_type_id?: string;
  product_name: string;
  status: string;
  created_at?: string;
}
// ✅ Auth header
const getAuthHeaders = () => {
  const token = localStorage.getItem("authToken") || "";
  return {
    Authorization: token,
    Accept: "application/json",
  };
};

// ✅ Error handler
const handleAxiosError = (
  error: any,
  fallbackMessage = "Something went wrong"
) => {
  if (axios.isAxiosError(error)) {
    const message =
      error.response?.data?.details ||
      error.response?.data?.message ||
      fallbackMessage;

    toast.error(message);
  } else {
    toast.error(fallbackMessage);
  }
};
// ✅ Fetch all product types
export const fetchAllProductTypes = async (): Promise<ProductType[]> => {
  try {
    const response = await axios.get(`${BASE_URL}/`, {
      headers: getAuthHeaders(),
    });
    return response.data;
  } catch (error: any) {
    const errorMessage =
      error?.response?.data?.details || error?.response?.data?.message || "Failed to fetch product types.";
    toast.error(errorMessage);
    return [];
  }
};
 
export const fetchAllProductTypesActive = async (): Promise<ProductType[]> => {
  try {
    const response = await axios.get(`${BASE_URL}/only/active`, {
      headers: getAuthHeaders(),
    });
    return response.data;
  } catch (error: any) {
    const errorMessage =
      error?.response?.data?.details || error?.response?.data?.message || "Failed to fetch product types.";
    toast.error(errorMessage);
    return [];
  }
};
// ✅ Create new product type
export const createProductType = async (data: ProductType): Promise<boolean> => {
  try {
    const formData = new URLSearchParams();
    formData.append("product_name", data.product_name);
    formData.append("status", data.status);
    const response = await axios.post(`${BASE_URL}/create`, formData, {
      headers: {
        "Content-Type": "application/x-www-form-urlencoded",
        ...getAuthHeaders(),
      },
    });
    toast.success("Product Type created successfully!");
    return !!response.data;
  } catch (error: any) {
    const errorMessage =
      error?.response?.data?.details || error?.response?.data?.message || "Failed to create product type.";
    toast.error(errorMessage);
    return false;
  }
};
 
// ✅ Update product type
export const updateProductType = async (
  id: string,
  data: ProductType
): Promise<boolean> => {
  try {
    const formData = new URLSearchParams();
    formData.append("product_name", data.product_name);
    formData.append("status", data.status);
    const response = await axios.put(`${BASE_URL}/update/${id}`, formData, {
      headers: {
        "Content-Type": "application/x-www-form-urlencoded",
        ...getAuthHeaders(),
      },
    });
    toast.success("Product Type updated successfully!");
    return !!response.data;
  } catch (error: any) {
    const errorMessage =
      error?.response?.data?.details || error?.response?.data?.message || "Failed to update product type.";
    toast.error(errorMessage);
    return false;
  }
};
 
// ✅ Delete product type
export const deleteProductType = async (id: string): Promise<boolean> => {
  try {
    await axios.delete(`${BASE_URL}/delete/${id}`, {
      headers: getAuthHeaders(),
    });
    toast.success("Product Type deleted successfully!");
    return true;
  } catch (error: any) {
    const errorMessage =
      error?.response?.data?.details || error?.response?.data?.message || "Failed to delete product type.";
    toast.error(errorMessage);
    return false;
  }
};


/* =====================================================
   EXPORT – ALL
===================================================== */

// ✅ Export all Product Types – Excel
export const exportProductTypeExcel = async (): Promise<Blob> => {
  try {
    const res = await axios.get(
      `${API_BASE_PATH}/producttype_export_all_excel`,
      {
        responseType: "blob",
        headers: getAuthHeaders(),
      }
    );

    toast.success("Product types exported as Excel successfully");
    return res.data;
  } catch (error) {
    handleAxiosError(error, "Failed to export product types (Excel)");
    throw error;
  }
};

// ✅ Export all Product Types – PDF
export const exportProductTypePDF = async (): Promise<Blob> => {
  try {
    const res = await axios.get(
      `${API_BASE_PATH}/producttype_export_all_pdf`,
      {
        responseType: "blob",
        headers: getAuthHeaders(),
      }
    );

    toast.success("Product types exported as PDF successfully");
    return res.data;
  } catch (error) {
    handleAxiosError(error, "Failed to export product types (PDF)");
    throw error;
  }
};

/* =====================================================
   EXPORT – SELECTED
===================================================== */

// ✅ Export selected Product Types – Excel
export const exportSelectedProductTypeExcel = async (
  ids: string[]
): Promise<Blob> => {
  try {
    const res = await axios.get(
      `${API_BASE_PATH}/producttype_export_selected_excel`,
      {
        params: { ids: ids.join(",") },
        responseType: "blob",
        headers: getAuthHeaders(),
      }
    );

    toast.success("Selected product types exported as Excel successfully");
    return res.data;
  } catch (error) {
    handleAxiosError(error, "Failed to export selected product types (Excel)");
    throw error;
  }
};

// ✅ Export selected Product Types – PDF
export const exportSelectedProductTypePDF = async (
  ids: string[]
): Promise<Blob> => {
  try {
    const res = await axios.get(
      `${API_BASE_PATH}/producttype_export_selected_pdf`,
      {
        params: { ids: ids.join(",") },
        responseType: "blob",
        headers: getAuthHeaders(),
      }
    );

    toast.success("Selected product types exported as PDF successfully");
    return res.data;
  } catch (error) {
    handleAxiosError(error, "Failed to export selected product types (PDF)");
    throw error;
  }
};

/* =====================================================
   BULK DELETE
===================================================== */

// ✅ Bulk delete Product Types
export const deleteProductTypeBulk = async (
  ids: string[]
): Promise<void> => {
  try {
    const res = await axios.delete(
      `${API_BASE_PATH}/producttype_bulk_delete`,
      {
        params: { ids: ids.join(",") },
        headers: getAuthHeaders(),
      }
    );

    // toast.success(res.data?.message || "Product types deleted successfully");
  } catch (error: any) {
    if (error?.response?.status === 403) {
      console.error("You are not authorized to delete product types.");
    } else {
      handleAxiosError(error, "Failed to delete product types");
    }
    throw error;
  }
};

/* =====================================================
   IMPORT – EXCEL
===================================================== */

export interface ProductTypeImportResult {
  message: string;
  added: {
    product_type_name: string;
    product_type_status: string;
  }[];
  skipped: {
    row: number;
    product_type_name: string;
    reason: string;
  }[];
}

// ✅ Import Product Type Excel
export const importProductTypeExcel = async (
  file: File
): Promise<ProductTypeImportResult> => {
  try {
    const formData = new FormData();
    formData.append("file", file);

    const res = await axios.post(
      `${API_BASE_PATH}/producttype_import_excel`,
      formData,
      {
        headers: {
          ...getAuthHeaders(),
          "Content-Type": "multipart/form-data",
        },
      }
    );

    toast.success(res.data?.message || "Product type import completed");
    return res.data;
  } catch (error) {
    handleAxiosError(error, "Failed to import product types");
    throw error;
  }
};